import {
  fetchShipmentParcels,
  handleCreateShipmentOrder,
  CONTACT_KEY,
  DEFAULT_CONTACT,
  DEFAULT_DIMENSIONS,
  MISSING_FIELD_LIST,
  SHIPPING_ENUM,
} from '@/entities/rma';
import { fetchStudyInfoClinic } from '@/entities/study';
import { SIGNATURE_ENUM } from '@/shared/config';
import { auth, handleFetchDivisions, showMessageError, handleFetchCountries } from '@/shared/lib';
import { useDebounceEffect, useElementChange, useSetState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDefaultState } from '../../lib/createReturnShipping';

/**
 * Drives the create-return-shipping page of a study: shipment date, sender and recipient contacts, signature confirmation, carrier rates and the page modals. Loads study and address data whenever studyId changes, keeps the header's has-login-banner class in sync, re-requests rates through a debounced effect on date, contact, signature or dangerous-goods changes, and exposes the state with its change and toggle handlers.
 *
 * @returns Return shipping state, header ref, missing-data flag and the form handlers.
 */
const useCreateReturnShipping = () => {
  const { studyId } = useParams();

  const navigate = useNavigate();

  const isFetchShipmentParcels = useRef(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const isChangePosition = useElementChange(headerRef);

  const [state, setState] = useSetState<any>({
    isLoadedData: true,
    isLoadedRate: true,
    study: {},
    shipmentDate: dayjs(),
    senderContact: {
      ...DEFAULT_CONTACT,
    },
    recipientContact: {
      ...DEFAULT_CONTACT,
    },
    signatureConfirmation: '',
    dangerousGoods: true,
    listStatesUS: [],
    listStatesCA: [],
    listCountries: [],
    rates: [],
    rateId: '',
    shipmentParcels: [],
    openEditShipmentModal: '',
    isOpenMissingModal: false,
    missingModalData: [],
    isOpenPreviewShipmentModal: false,
  });

  /**
   * Reports whether the return shipping form still lacks data required to request carrier rates. Memoized on shipment date and both contacts: true when no shipmentDate, or when any required CONTACT_KEY except street2 and company is empty on the sender or recipient contact.
   *
   * @returns True while required shipment data is incomplete.
   */
  const isMissingData = useMemo(() => {
    if (!state.shipmentDate) {
      return true;
    }
    const listRequiredKeys = _.difference(_.values(CONTACT_KEY), ['street2', 'company']);

    /**
     * Flags whether either the sender or recipient contact is missing any required address field. Computed with lodash some over listRequiredKeys, true as soon as one key is falsy on state.recipientContact or state.senderContact.
     *
     * @returns True when at least one required contact field is empty.
     */
    const isInvalidShipment = _.some(listRequiredKeys, (key) => {
      if (!state.recipientContact?.[key] || !state.senderContact?.[key]) {
        return true;
      }
      return false;
    });

    if (isInvalidShipment) {
      return true;
    }

    return false;
  }, [state.shipmentDate, state.senderContact, state.recipientContact]);

  /**
   * Returns the user from the return shipping form back to the owning study. Navigates to `/studies/{studyId}` with router state shouldNavigateToReturnStatusTab so the study page opens on its return status tab.
   *
   * @returns Nothing.
   */
  const handleClickBack = () => {
    navigate(`/studies/${studyId}`, {
      state: {
        shouldNavigateToReturnStatusTab: true,
      },
    });
  };

  /**
   * Records which carrier rate the return shipment will be created with. Stores rateId in the hook state.
   *
   * @param rateId - Identifier of the selected carrier rate.
   * @returns Nothing.
   */
  const onChangeRate = (rateId: any) => {
    setState({ rateId });
  };

  /**
   * Sets the requested ship date of the return shipment. Writes date into state.shipmentDate.
   *
   * @param date - Selected shipment date.
   * @returns Nothing.
   */
  const onChangeShipmentDate = (date: any) => {
    setState({ shipmentDate: date });
  };

  /**
   * Turns signature confirmation for the return shipment on or off. Clears state.signatureConfirmation when it already holds a value, otherwise sets it to SIGNATURE_ENUM.STANDARD.
   *
   * @returns Nothing.
   */
  const onChangeSignature = () => {
    if (state.signatureConfirmation) {
      setState({ signatureConfirmation: '' });
    } else {
      setState({ signatureConfirmation: SIGNATURE_ENUM.STANDARD });
    }
  };

  /**
   * Stores the signature confirmation option picked for the return shipment. Writes value into state.signatureConfirmation and ignores the name argument.
   *
   * @param name - Field name from the option control; unused.
   * @param value - Signature confirmation option to store.
   * @returns Nothing.
   */
  const onChangeOptionSignature = (name: any, value: any) => {
    setState({ signatureConfirmation: value });
  };

  /**
   * Applies a checkbox change on the return shipping form to the matching state field. Merges an object keyed by name holding value into the hook state.
   *
   * @param name - State key to update.
   * @param value - New value stored under that key.
   * @returns Nothing.
   */
  const onChangeCheckBox = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Opens or closes the sender and recipient contact editor of the return shipping form. Stores a truthy value as openEditShipmentModal and clears the field to an empty string otherwise.
   *
   * @param value - Editor mode to open, or falsy to close.
   * @returns Nothing.
   */
  const toggleEditShipmentModal = (value: any) => {
    if (value) {
      setState({ openEditShipmentModal: value });
    } else {
      setState({ openEditShipmentModal: '' });
    }
  };

  /**
   * Shows or hides the shipment preview modal for the return label. Inverts state.isOpenPreviewShipmentModal.
   *
   * @returns Nothing.
   */
  const togglePreviewShipmentModal = () => {
    setState({ isOpenPreviewShipmentModal: !state.isOpenPreviewShipmentModal });
  };

  /**
   * Shows or hides the missing-contact-information modal. Inverts state.isOpenMissingModal.
   *
   * @returns Nothing.
   */
  const toggleMissingModal = () => {
    setState({ isOpenMissingModal: !state.isOpenMissingModal });
  };

  /**
   * Confirms the missing-contact-information prompt and opens the matching contact editor. Closes the missing modal and sets openEditShipmentModal to SHIPPING_ENUM.FIRST_EDIT_SENDER when missingModalData contains MISSING_FIELD_LIST.SENDER_EMAIL, otherwise to FIRST_EDIT_RECIPIENT.
   *
   * @returns Nothing.
   */
  const onSubmitMissingModal = () => {
    setState({
      isOpenMissingModal: false,
      openEditShipmentModal: _.includes(state.missingModalData, MISSING_FIELD_LIST.SENDER_EMAIL)
        ? SHIPPING_ENUM.FIRST_EDIT_SENDER
        : SHIPPING_ENUM.FIRST_EDIT_RECIPIENT,
    });
  };

  /**
   * Merges edited contact fields into the sender or recipient contact of the return shipment. Targets senderContact when openEditShipmentModal is SENDER or FIRST_EDIT_SENDER and recipientContact otherwise, spreads data over that contact and closes the editor, then reopens it on the recipient after a 500ms animation delay when the sender was being edited for the first time.
   *
   * @param data - Edited contact fields to merge.
   * @returns Nothing.
   */
  const updateShipment = (data: any) => {
    const key = [SHIPPING_ENUM.SENDER, SHIPPING_ENUM.FIRST_EDIT_SENDER].includes(
      state.openEditShipmentModal,
    )
      ? 'senderContact'
      : 'recipientContact';
    const isFirstEditSender = state.openEditShipmentModal === SHIPPING_ENUM.FIRST_EDIT_SENDER;
    setState({
      [key]: {
        ...state[key],
        ...data,
      },
      openEditShipmentModal: '',
    });
    if (isFirstEditSender) {
      setTimeout(() => {
        setState({
          openEditShipmentModal: SHIPPING_ENUM.FIRST_EDIT_RECIPIENT,
        });
        // 500ms are animation time of edit shipment modal
      }, 500);
    }
  };

  /**
   * Loads the study and address reference data the patient-return shipping page needs. Fetches the clinic study info, the country list and both CA and US divisions in parallel, throws when the study or its patientReturn is empty, seeds shipment date and contacts from getDefaultState and opens the missing-information modal when fields are absent; on error it logs, redirects to /studies and shows the message.
   *
   * @returns Resolves once the state is populated or the redirect has been issued.
   */
  const fetchData = async () => {
    try {
      setState({ isLoadedData: false });
      const [study, listCountries, listStatesCA, listStatesUS] = await Promise.all([
        fetchStudyInfoClinic(studyId),
        handleFetchCountries(),
        handleFetchDivisions('CA'),
        handleFetchDivisions('US'),
      ]);

      if (!study || _.isEmpty(study?.patientReturn)) {
        throw new Error('Failed to fetch study!');
      }

      // *: For testing
      // study.timezone = 'UTC-05';

      const { shipmentDate, senderContact, recipientContact, missingModalData } =
        getDefaultState(study);

      const setStateObject = {
        isLoadedData: true,
        study,
        shipmentDate,
        senderContact,
        recipientContact,
        listCountries,
        listStatesCA,
        listStatesUS,
        missingModalData,
        isOpenMissingModal: !_.isEmpty(missingModalData),
      };
      setState(setStateObject);
    } catch (error) {
      console.error('Failed to fetch study: ', error);
      navigate('/studies', { replace: true });
      showMessageError({ content: error instanceof Error ? error.message : '' });
    }
  };

  /**
   * Loads the shipment parcel options for the return shipment at most once per hook instance. Returns null immediately when the isFetchShipmentParcels ref is already set, otherwise marks the ref and awaits fetchShipmentParcels; a failure is logged, resets the ref and yields null.
   *
   * @returns The fetched shipment parcels, or null when a fetch already ran or the request failed.
   */
  const handleFetchShipmentParcels = async () => {
    try {
      if (isFetchShipmentParcels.current) {
        return null;
      }
      isFetchShipmentParcels.current = true;
      const shipmentParcels = await fetchShipmentParcels();
      return shipmentParcels;
    } catch (error) {
      console.error('Failed to fetch shipment parcels: ', error);
      isFetchShipmentParcels.current = false;
      return null;
    }
  };

  /**
   * Requests carrier rates for the patient-return shipment from the current form values. Returns early while isMissingData, builds a 'Patient Return' shipment order input from both contacts, DEFAULT_DIMENSIONS, the dangerous-goods flag and any signature confirmation, creates the order and fetches parcels in parallel, then stores rates sorted by first attribute with the first preselected; errors leave the rate list empty.
   *
   * @returns Resolves once the rates or the error fallback are stored.
   */
  const fetchShipmentOrder = async () => {
    if (isMissingData) {
      return;
    }
    try {
      setState({ isLoadedRate: false });
      const input: any = {
        ticketId: state.study?.patientReturn?.id,
        ticketType: 'Patient Return',
        shipmentDate: state.shipmentDate.toISOString(),
        senderContact: {
          ...state.senderContact,
        },
        recipientContact: {
          ...state.recipientContact,
        },
        // Apply change follow BA+BE request
        // parcelId: patientReturnConfig?.packageShipmentLabel,
        customDimensions: DEFAULT_DIMENSIONS,
        dangerousGoods: state.dangerousGoods,
      };
      if (state.signatureConfirmation) {
        input.signatureConfirmation = state.signatureConfirmation;
      }

      const [shipmentOrder, shipmentParcels] = await Promise.all([
        handleCreateShipmentOrder(input),
        handleFetchShipmentParcels(),
      ]);
      const sortRates = _.sortBy(shipmentOrder?.rates, [(d) => d?.attributes?.[0]]);

      const objectState = {
        isLoadedRate: true,
        rates: sortRates,
        rateId: sortRates[0]?.id,
      };
      if (shipmentParcels) {
        _.assign(objectState, {
          shipmentParcels,
        });
      }

      setState(objectState);
    } catch (error) {
      console.error('Failed to create shipment order: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
      setState({ isLoadedRate: true, rates: [], rateId: '' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [studyId]);

  useEffect(() => {
    if (headerRef.current) {
      if (auth.isShowLoginByBanner()) {
        headerRef.current.classList.add('has-login-banner');
      } else {
        headerRef.current.classList.remove('has-login-banner');
      }
    }
  }, [headerRef.current, isChangePosition]);

  useDebounceEffect(() => {
    fetchShipmentOrder();
  }, [
    state.shipmentDate,
    state.senderContact,
    state.recipientContact,
    state.signatureConfirmation,
    isMissingData,
    state.dangerousGoods,
  ]);

  return {
    state,
    headerRef,
    isMissingData,
    handleClickBack,
    onChangeRate,
    onChangeShipmentDate,
    onChangeSignature,
    onChangeOptionSignature,
    onChangeCheckBox,
    toggleEditShipmentModal,
    togglePreviewShipmentModal,
    toggleMissingModal,
    onSubmitMissingModal,
    updateShipment,
  };
};

export { useCreateReturnShipping };
